import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Grid } from '@material-ui/core'

import NumberFormat from 'react-number-format'
// import { reviewCols, certificatesCols } from '../constants/EmployeeCols'
import { AntTabs, AntTab } from '../components/common/Tabs'
import ReactTable from '../components/ReactTable'
import PersonalActHistory from '../components/PersonalActHisotry'
import {
  useCertificatesCols,
  useReviewCols,
  useDisciplinaryCols,
  useEducationCols,
  useCertCols,
  useCompetenciesCols
} from '../hooks/EmployeeCols'
import EmployeeComments from './EmployeeComments';
import Competencies from "./Competencies";
import fetchFromAthena from '../functions/FetchFromAthena'

export default function PersonalActions (props) {
  const [value, setValue] = React.useState(0)
  const [tabData, setTabData] = useState({
    reviews: [],
    certificates: [],
    disciplinaryActions: []
  })
  const [loadedTabs, setLoadedTabs] = useState(new Set())
  const [loadingTabs, setLoadingTabs] = useState(new Set())
  const loadedTabsRef = useRef(new Set())
  const loadingTabsRef = useRef(new Set())

  // Sync refs with state
  useEffect(() => {
    loadedTabsRef.current = loadedTabs
  }, [loadedTabs])

  useEffect(() => {
    loadingTabsRef.current = loadingTabs
  }, [loadingTabs])

  const reviewCols = useReviewCols()
  const certificatesCols = useCertificatesCols()
  const disciplinaryCols = useDisciplinaryCols()
  const eduCols = useEducationCols()
  const certCols = useCertCols()
  const competenciesCols = useCompetenciesCols()

  const handleChange = (event, newValue) => {
    setValue(newValue)
  }

  // Centralized permission helper
  const allow = props?.fetchInitialData?.credentials?.allow || (() => false)
  const allowPA = useCallback((subkey) => allow('subpage', 'HR', subkey), [allow])

  const loadTabData = useCallback(async (tabValue) => {
    if (!props.empId || !props.company) {
      return
    }

    // Prevent duplicate calls
    if (loadedTabsRef.current.has(tabValue) || loadingTabsRef.current.has(tabValue)) {
      return
    }

    // Mark tab as loading
    setLoadingTabs(prev => new Set([...prev, tabValue]))

    const tabQueries = []
    const credentials = props.fetchInitialData.credentials

    // Map tab values to their queries
    if (tabValue === 1 && allowPA('personnel-action-reviews')) {
      tabQueries.push({ 
        query: `employee_reviews_read${allowPA('personnel-action-reviews') ? '' : 'false'}`, 
        key: 'reviews' 
      })
    }
    if (tabValue === 2 && allowPA('personnel-action-certifications')) {
      // Education uses certificates data filtered by TYPE === 'ED'
      tabQueries.push({ 
        query: `employee_certifications_read${allowPA('personnel-action-certifications') ? '' : 'false'}`, 
        key: 'certificates' 
      })
    }
    if (tabValue === 3 && allowPA('personnel-action-certifications')) {
      // Certifications uses certificates data filtered by TYPE === 'CE' or 'LC'
      tabQueries.push({ 
        query: `employee_certifications_read${allowPA('personnel-action-certifications') ? '' : 'false'}`, 
        key: 'certificates' 
      })
    }
    if (tabValue === 4 && allowPA('personnel-action-disciplinary')) {
      tabQueries.push({ 
        query: `employee_disciplinary_actions${allowPA('personnel-action-disciplinary') ? '' : 'false'}`, 
        key: 'disciplinaryActions' 
      })
    }

    if (tabQueries.length === 0) {
      // Tab doesn't need data fetching
      setLoadedTabs(prev => new Set([...prev, tabValue]))
      setLoadingTabs(prev => {
        const next = new Set(prev)
        next.delete(tabValue)
        return next
      })
      return
    }

    const data = await Promise.allSettled(
      tabQueries.map(({ query }) =>
        fetchFromAthena({
          queryName: query,
          headers: {
            company: props.company,
            empId: props.empId
          },
          accessToken: credentials.user.accessToken,
          credentials: credentials
        })
      )
    )

    const updates = {}
    tabQueries.forEach(({ key }, index) => {
      const result = data[index]?.value || []
      updates[key] = result
    })

    setTabData(prevData => ({
      ...prevData,
      ...updates
    }))
    setLoadedTabs(prev => new Set([...prev, tabValue]))
    setLoadingTabs(prev => {
      const next = new Set(prev)
      next.delete(tabValue)
      return next
    })
  }, [props.empId, props.company, props.fetchInitialData.credentials, allowPA])

  // Load data when tab changes
  useEffect(() => {
    if (props.empId && !loadedTabsRef.current.has(value) && !loadingTabsRef.current.has(value)) {
      loadTabData(value)
    }
  }, [value, props.empId, loadTabData])

  const constructPersonnelTabs = () => {
    const permissions = {}
    const tabs = []
    if (allowPA('personnel-action-history')) {
      tabs.push({
        display: 'History',
        value: 0
      })
      permissions['main'] = true
    }
    if (allowPA('personnel-action-reviews')) {
      tabs.push({
        display: 'Reviews',
        value: 1
      })
      permissions['reviews'] = true
    }
    if (allowPA('personnel-action-certifications')) {
      tabs.push({
        display: 'Education',
        value: 2
      })
      permissions['certifications'] = true
    }
    if (allowPA('personnel-action-certifications')) {
      tabs.push({
        display: 'Certifications',
        value: 3
      })
      permissions['certifications'] = true
    }
    if (allowPA('personnel-action-disciplinary')) {
      tabs.push({
        display: 'Disciplinary Actions',
        value: 4
      })
      permissions['certifications'] = true
    }
    if (allowPA('personnel-action-comments')) {
      tabs.push({
        display: 'Comments',
        value: 5
      })
      permissions['comments'] = true
    }
    if (allowPA('personnel-action-competencies')) {
      tabs.push({
        display: 'Competencies',
        value: 6
      })
      permissions['competencies'] = true
    }
    return { tabs, permissions }
  }
  const { tabs, permissions } = constructPersonnelTabs()

  // Use tabData if the tab has been loaded, otherwise fall back to props.data
  const reviewsData = loadedTabs.has(1) ? tabData.reviews : (props.data.reviews || [])
  const certificatesData = (loadedTabs.has(2) || loadedTabs.has(3)) ? tabData.certificates : (props.data.certificates || [])
  const disciplinaryActionsData = loadedTabs.has(4) ? tabData.disciplinaryActions : (props.data.disciplinaryActions || [])

  const certData = certificatesData.filter(cur => cur.TYPE === 'CE' || cur.TYPE === 'LC')
  const eduData = certificatesData.filter(cur => cur.TYPE === 'ED')


  return (
    <div>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <div>
            <AntTabs
              color={props.fetchInitialData.credentials.primaryAppColor}
              value={value}
              onChange={handleChange}
              aria-label='ant example'
            >
              {tabs.map((cur,index) => {
                return (
                  <AntTab
                    key={index}
                    label={cur.display}
                    value={cur.value}
                    color={props.fetchInitialData.credentials.primaryAppColor}
                  />
                )
              })}
            </AntTabs>
            {value === 0 && (
              <div>
                <PersonalActHistory
                  data={props.data.actions || []}
                  empId={props.empId}
                  fileName={`Personnel_Action_History_${props.employee.FIRST_NAME}_${props.employee.LAST_NAME}`}
                  company={props.company}
                  accessToken={
                    props.fetchInitialData.credentials.user.accessToken
                  }
                  fetchInitialData={props.fetchInitialData}
                  isLoading={props.isLoading}
                />
              </div>
            )}
            {value === 1 && (
              <div>
                <ReactTable 
                  cols={reviewCols} 
                  data={reviewsData}
                  isLoading={props.isLoading || loadingTabs.has(1)}
                  initialState={{
                    sorting: [
                      {
                        id: 'REVIEW_DATE',
                        desc: true // Most recent first (descending order)
                      }
                    ]
                  }}
                />
              </div>
            )}
            {value === 2 && (
              <div>
                <ReactTable
                  cols={eduCols}
                  data={eduData}
                  isLoading={props.isLoading || loadingTabs.has(2)}
                />
              </div>
            )}
            {value === 3 && (
              <div>
                <ReactTable
                  cols={certCols}
                  data={certData}
                  isLoading={props.isLoading || loadingTabs.has(3)}
                />
              </div>
            )}
            {value === 4 && (
              <div>
                <ReactTable
                  cols={disciplinaryCols}
                  data={disciplinaryActionsData}
                  isLoading={props.isLoading || loadingTabs.has(4)}
                />
              </div>
            )}
            {value === 5 && (
              <>
              <EmployeeComments 
                key={`comments-${props.empId}-${value}`}
                empId={props.empId}
                company={props.company}
                credentials={props.fetchInitialData.credentials}
                />
                </>
            )}
            {value === 6 && (
              <div>
                <Competencies
                  key={`competencies-${props.empId}-${value}`}
                  empId={props.empId}
                  company={props.company}
                  credentials={props.fetchInitialData.credentials}
                />
              </div>
            )}
          </div>
        </Grid>
      </Grid>
    </div>
  )
}
